import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Wine, Trash2, Minus, Plus, MapPin, Clock, CreditCard, CheckCircle, Tag, Award, ShieldAlert, Truck, Store, DollarSign } from 'lucide-react';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';

const TIP_OPTIONS = [
  { label: '15%', value: 0.15 },
  { label: '18%', value: 0.18 },
  { label: '20%', value: 0.20 },
  { label: '25%', value: 0.25 },
  { label: 'No Tip', value: 0 },
];

export default function CheckoutPage() {
  const { items, removeItem, updateQuantity, clearCart, subtotal, hasAlcohol } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState('cart'); // cart | checkout | confirmation
  const [address, setAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [tipPercent, setTipPercent] = useState(0.18);
  const [customTip, setCustomTip] = useState('');
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [customerName, setCustomerName] = useState(user?.name || '');
  const [customerPhone, setCustomerPhone] = useState(user?.phone || '');
  const [placing, setPlacing] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponApplied, setCouponApplied] = useState(null);
  const [loyaltyPoints, setLoyaltyPoints] = useState(0);
  const [redeemPoints, setRedeemPoints] = useState(0);
  const [fulfillment, setFulfillment] = useState('delivery'); // delivery | pickup
  const [paymentMethod, setPaymentMethod] = useState('cod'); // cod only
  const [storeInfo, setStoreInfo] = useState({ tax_enabled: true, tax_rate: 0.06625, tax_label: 'Sales Tax', delivery_fee: 4.99, pickup_enabled: true, cod_enabled: true, online_enabled: true });

  useEffect(() => {
    if (user) {
      api.get('/loyalty/status').then(r => setLoyaltyPoints(r.data.points)).catch(() => {});
    }
    api.get('/store-info').then(r => {
      const s = r.data;
      setStoreInfo(prev => ({ ...prev, ...s }));
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (searchParams.get('payment') === 'cancel') {
      toast.error('Payment was cancelled. You can retry with Card or Cash.');
    }
  }, [searchParams]);

  const loyaltyDiscount = Math.round(redeemPoints / 100 * 100) / 100;
  const taxable = Math.max(0, subtotal - couponDiscount - loyaltyDiscount);
  const tax = storeInfo.tax_enabled ? Math.round(taxable * (storeInfo.tax_rate || 0) * 100) / 100 : 0;
  const isPickup = fulfillment === 'pickup';
  const deliveryFee = isPickup ? 0 : (couponApplied?.type === 'free_delivery' ? 0 : (storeInfo.delivery_fee ?? 4.99));
  const tipValue = isPickup ? 0 : (customTip ? parseFloat(customTip) || 0 : Math.round(subtotal * tipPercent * 100) / 100);
  const total = Math.round((taxable + tax + deliveryFee + tipValue) * 100) / 100;

  const validateCoupon = async () => {
    if (!promoCode.trim()) return;
    try {
      const res = await api.post('/coupons/validate', { code: promoCode, subtotal });
      setCouponDiscount(res.data.discount);
      setCouponApplied(res.data);
      toast.success(`Coupon applied: ${res.data.description || ''}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid coupon');
      setCouponDiscount(0);
      setCouponApplied(null);
    }
  };

  const handlePlaceOrder = async () => {
    if (fulfillment === 'delivery' && !address.trim()) return toast.error('Please enter a delivery address');
    if (!user && !customerName.trim()) return toast.error('Please enter your name');
    if (hasAlcohol && !ageConfirmed) return toast.error('You must confirm you are 21+ to order alcohol');

    setPlacing(true);
    try {
      // Idempotency key — same key across retries means "give me the same order, not a duplicate"
      const idemKey = `ord-${user?.id || 'guest'}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const res = await api.post('/orders', {
        items: items.map(i => ({ product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity, image: i.image })),
        delivery_address: fulfillment === 'delivery' ? address : '',
        notes,
        tip: tipValue,
        age_confirmed: ageConfirmed,
        customer_name: customerName,
        customer_phone: customerPhone,
        promo_code: couponApplied ? promoCode : '',
        loyalty_points_redeem: redeemPoints,
        fulfillment_type: fulfillment,
        payment_method: paymentMethod,
      }, { headers: { 'X-Idempotency-Key': idemKey } });

      if (paymentMethod === 'online' && res.data.stripe_session_url) {
        clearCart();
        window.location.href = res.data.stripe_session_url;
        return;
      }

      // COD path (no redirect)
      setConfirmationData(res.data);
      clearCart();
      setStep('confirmation');
      toast.success('Order placed successfully!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to place order');
    } finally {
      setPlacing(false);
    }
  };

  if (step === 'confirmation' && confirmationData) {
    return (
      <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center px-4" data-testid="order-confirmation">
        <div className="max-w-md w-full bg-[#12151C] border border-white/10 rounded-2xl p-8 text-center animate-fade-in-up">
          <div className="w-16 h-16 rounded-full bg-[#10B981]/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-[#10B981]" />
          </div>
          <h2 className="font-['Playfair_Display'] text-2xl font-medium text-white mb-2">Order Confirmed!</h2>
          <p className="text-[#b56b38] font-medium text-lg mb-1">#{confirmationData.order_number}</p>
          <p className="text-[#94A3B8] font-light mb-6">Your order is being prepared and will be delivered soon.</p>
          <div className="bg-[#0B0E14] border border-white/5 rounded-xl p-4 mb-6 text-left">
            <div className="flex justify-between text-sm mb-2"><span className="text-[#64748B]">Items</span><span className="text-white">{confirmationData.items?.length}</span></div>
            <div className="flex justify-between text-sm mb-2"><span className="text-[#64748B]">Total</span><span className="text-[#b56b38] font-medium">${confirmationData.total?.toFixed(2)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-[#64748B]">Delivery</span><span className="text-white">{confirmationData.delivery_address}</span></div>
          </div>
          <div className="flex gap-3">
            <Link to={`/order/${confirmationData.id}`} className="flex-1 bg-[#cc3737] hover:bg-[#d94444] text-white rounded-full py-3 font-medium transition-colors" data-testid="track-order-btn">Track Order</Link>
            <Link to="/shop" className="flex-1 border border-white/20 hover:border-white text-white rounded-full py-3 font-medium transition-all">Continue Shopping</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0E14]" data-testid="checkout-page">
      {/* Header */}
      <nav className="fixed top-0 w-full z-50 bg-[#0B0E14]/70 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex items-center justify-between h-16">
          <button onClick={() => step === 'checkout' ? setStep('cart') : navigate(-1)} className="flex items-center gap-2 text-[#94A3B8] hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" /> {step === 'checkout' ? 'Back to Cart' : 'Back'}
          </button>
          <h1 className="font-['Playfair_Display'] text-lg text-white">{step === 'cart' ? 'Your Cart' : 'Checkout'}</h1>
          <div className="w-16" />
        </div>
      </nav>

      <div className="pt-24 pb-12 max-w-4xl mx-auto px-6 sm:px-8">
        {items.length === 0 && step === 'cart' ? (
          <div className="text-center py-20 animate-fade-in">
            <div className="w-20 h-20 rounded-full bg-[#12151C] flex items-center justify-center mx-auto mb-6">
              <Wine className="w-8 h-8 text-[#64748B]" />
            </div>
            <h2 className="font-['Playfair_Display'] text-2xl text-white mb-3">Your cart is empty</h2>
            <p className="text-[#64748B] mb-6">Browse our selection and add some items</p>
            <Link to="/shop" className="bg-[#cc3737] hover:bg-[#d94444] text-white rounded-full px-8 py-3 font-medium transition-colors inline-block" data-testid="browse-shop-btn">Browse Shop</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Items */}
            <div className="lg:col-span-3 space-y-3">
              {step === 'cart' && items.map((item, i) => (
                <div key={item.cart_key} className="bg-[#12151C] border border-white/5 rounded-xl p-4 flex gap-4 animate-fade-in-up" style={{ animationDelay: `${i * 0.05}s`, opacity: 0 }} data-testid={`cart-item-${i}`}>
                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-[#1A1D27] flex-shrink-0">
                    {item.image && <img src={item.image} alt={item.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="text-white text-sm font-medium truncate">{item.name}{item.variant_name ? ` (${item.variant_name})` : ''}</h3>
                        <p className="text-[#b56b38] text-sm font-medium">${item.price.toFixed(2)}</p>
                      </div>
                      <button onClick={() => removeItem(item.cart_key)} className="text-[#64748B] hover:text-[#EF4444] transition-colors" data-testid={`remove-item-${i}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <button onClick={() => updateQuantity(item.cart_key, item.quantity - 1)} className="w-6 h-6 rounded bg-[#0B0E14] flex items-center justify-center text-[#94A3B8] hover:text-white"><Minus className="w-3 h-3" /></button>
                      <span className="text-white text-sm w-6 text-center">{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.cart_key, item.quantity + 1)} className="w-6 h-6 rounded bg-[#0B0E14] flex items-center justify-center text-[#94A3B8] hover:text-white"><Plus className="w-3 h-3" /></button>
                      <span className="text-[#64748B] text-xs ml-auto">${(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              ))}

              {step === 'checkout' && (
                <div className="space-y-4 animate-fade-in">
                  {/* Fulfillment Type */}
                  <div className="bg-[#12151C] border border-white/5 rounded-xl p-5" data-testid="fulfillment-box">
                    <h3 className="text-white font-medium mb-4">How would you like to receive your order?</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <button onClick={() => setFulfillment('delivery')} className={`flex flex-col items-center gap-2 rounded-xl p-4 transition-all border-2 ${fulfillment === 'delivery' ? 'border-[#cc3737] bg-[#cc3737]/10' : 'border-white/10 bg-[#0B0E14] hover:border-white/20'}`} data-testid="fulfillment-delivery">
                        <Truck className={`w-6 h-6 ${fulfillment === 'delivery' ? 'text-[#cc3737]' : 'text-[#64748B]'}`} />
                        <span className={`text-sm font-medium ${fulfillment === 'delivery' ? 'text-white' : 'text-[#94A3B8]'}`}>Delivery</span>
                        <span className="text-[10px] text-[#64748B]">${(storeInfo.delivery_fee ?? 4.99).toFixed(2)} fee</span>
                      </button>
                      <button onClick={() => setFulfillment('pickup')} disabled={!storeInfo.pickup_enabled} className={`flex flex-col items-center gap-2 rounded-xl p-4 transition-all border-2 disabled:opacity-40 ${fulfillment === 'pickup' ? 'border-[#b56b38] bg-[#b56b38]/10' : 'border-white/10 bg-[#0B0E14] hover:border-white/20'}`} data-testid="fulfillment-pickup">
                        <Store className={`w-6 h-6 ${fulfillment === 'pickup' ? 'text-[#b56b38]' : 'text-[#64748B]'}`} />
                        <span className={`text-sm font-medium ${fulfillment === 'pickup' ? 'text-white' : 'text-[#94A3B8]'}`}>Pickup</span>
                        <span className="text-[10px] text-[#10B981]">FREE · No tip</span>
                      </button>
                    </div>
                    {isPickup && <p className="text-[#b56b38] text-xs mt-3 bg-[#b56b38]/10 border border-[#b56b38]/20 rounded-lg px-3 py-2"><Store className="w-3 h-3 inline mr-1" /> Show your 4-digit pickup code at the counter: <strong>{storeInfo.address || '98 Willow Ave, Hoboken, NJ'}</strong></p>}
                  </div>

                  {/* Customer Info */}
                  {!user && (
                    <div className="bg-[#12151C] border border-white/5 rounded-xl p-5">
                      <h3 className="text-white font-medium mb-4 flex items-center gap-2"><CreditCard className="w-4 h-4 text-[#b56b38]" /> Your Info</h3>
                      <div className="space-y-3">
                        <input value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Full Name" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-4 py-2.5 text-sm text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#b56b38]" data-testid="customer-name-input" />
                        <input value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} placeholder="Phone Number" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-4 py-2.5 text-sm text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#b56b38]" data-testid="customer-phone-input" />
                      </div>
                    </div>
                  )}

                  {/* Delivery Address — only for delivery */}
                  {!isPickup && (
                    <div className="bg-[#12151C] border border-white/5 rounded-xl p-5">
                      <h3 className="text-white font-medium mb-4 flex items-center gap-2"><MapPin className="w-4 h-4 text-[#b56b38]" /> Delivery Address</h3>
                      <input value={address} onChange={e => setAddress(e.target.value)} placeholder="Enter delivery address in Hoboken, NJ" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-4 py-2.5 text-sm text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#b56b38]" data-testid="address-input" />
                      <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Delivery notes (optional)" rows={2} className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-4 py-2.5 text-sm text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#b56b38] mt-3 resize-none" data-testid="notes-input" />
                    </div>
                  )}
                  {isPickup && (
                    <div className="bg-[#12151C] border border-white/5 rounded-xl p-5">
                      <h3 className="text-white font-medium mb-4 flex items-center gap-2"><Store className="w-4 h-4 text-[#b56b38]" /> Pickup Notes</h3>
                      <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any notes for the store? (optional)" rows={2} className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-4 py-2.5 text-sm text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#b56b38] resize-none" data-testid="notes-input" />
                    </div>
                  )}

                  {/* Payment Method */}
                  <div className="bg-[#12151C] border border-white/5 rounded-xl p-5" data-testid="payment-box">
                    <h3 className="text-white font-medium mb-4 flex items-center gap-2"><CreditCard className="w-4 h-4 text-[#b56b38]" /> Payment Method</h3>
                    {storeInfo.online_enabled ? (
                      <div className="space-y-3">
                        <button
                          type="button"
                          onClick={() => setPaymentMethod('cod')}
                          className={`w-full flex items-start gap-3 rounded-xl p-4 transition-all border-2 text-left ${paymentMethod === 'cod' ? 'border-[#cc3737] bg-[#cc3737]/10' : 'border-white/10 bg-[#0B0E14] hover:border-white/20'}`}
                          data-testid="payment-cod"
                        >
                          <DollarSign className={`w-5 h-5 mt-0.5 ${paymentMethod === 'cod' ? 'text-[#cc3737]' : 'text-[#64748B]'}`} />
                          <div>
                            <span className={`text-sm font-medium block ${paymentMethod === 'cod' ? 'text-white' : 'text-[#94A3B8]'}`}>
                              {isPickup ? 'Cash at Pickup' : 'Cash on Delivery'}
                            </span>
                            <span className="text-xs text-[#64748B] block mt-0.5">
                              Please have exact change ready.
                            </span>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setPaymentMethod('online')}
                          className={`w-full flex items-start gap-3 rounded-xl p-4 transition-all border-2 text-left ${paymentMethod === 'online' ? 'border-[#cc3737] bg-[#cc3737]/10' : 'border-white/10 bg-[#0B0E14] hover:border-white/20'}`}
                          data-testid="payment-online"
                        >
                          <CreditCard className={`w-5 h-5 mt-0.5 ${paymentMethod === 'online' ? 'text-[#cc3737]' : 'text-[#64748B]'}`} />
                          <div>
                            <span className={`text-sm font-medium block ${paymentMethod === 'online' ? 'text-white' : 'text-[#94A3B8]'}`}>
                              Pay Online (Stripe)
                            </span>
                            <span className="text-xs text-[#64748B] block mt-0.5">
                              Secure card payment via Stripe.
                            </span>
                          </div>
                        </button>
                      </div>
                    ) : (
                      <div className="bg-[#0B0E14] border border-white/10 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-1.5">
                          <DollarSign className="w-5 h-5 text-[#b56b38]" />
                          <span className="text-sm font-medium text-white">{isPickup ? 'Cash at Pickup' : 'Cash on Delivery'}</span>
                        </div>
                        <p className="text-[#94A3B8] text-xs">Please have exact change ready. {isPickup ? 'Store staff' : 'Driver'} will confirm cash received before closing the order.</p>
                      </div>
                    )}
                  </div>

                  {/* Tip — only for delivery */}
                  {!isPickup && (
                    <div className="bg-[#12151C] border border-white/5 rounded-xl p-5">
                      <h3 className="text-white font-medium mb-4">Tip Your Driver</h3>
                      <div className="flex flex-wrap gap-2">
                        {TIP_OPTIONS.map(opt => (
                          <button
                            key={opt.label}
                            onClick={() => { setTipPercent(opt.value); setCustomTip(''); }}
                            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${!customTip && tipPercent === opt.value ? 'bg-[#cc3737] text-white' : 'bg-[#0B0E14] text-[#94A3B8] border border-white/10 hover:border-white/20'}`}
                            data-testid={`tip-${opt.label.replace(/\s+/g, '-').toLowerCase()}`}
                          >
                            {opt.label}{opt.value > 0 && ` ($${(subtotal * opt.value).toFixed(2)})`}
                          </button>
                        ))}
                        <input value={customTip} onChange={e => { setCustomTip(e.target.value); setTipPercent(0); }} placeholder="Custom $" className="w-24 bg-[#0B0E14] border border-white/10 rounded-full px-4 py-2 text-sm text-white text-center focus:outline-none focus:border-[#b56b38]" data-testid="custom-tip-input" />
                      </div>
                    </div>
                  )}

                  {/* Coupon Code */}
                  <div className="bg-[#12151C] border border-white/5 rounded-xl p-5">
                    <h3 className="text-white font-medium mb-4 flex items-center gap-2"><Tag className="w-4 h-4 text-[#b56b38]" /> Promo Code</h3>
                    <div className="flex gap-2">
                      <input value={promoCode} onChange={e => setPromoCode(e.target.value)} placeholder="Enter code (e.g. WELCOME10)" className="flex-1 bg-[#0B0E14] border border-white/10 rounded-md px-4 py-2.5 text-sm text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#b56b38] uppercase" data-testid="promo-code-input" />
                      <button onClick={validateCoupon} className="bg-[#b56b38] hover:bg-[#E6C65A] text-[#0B0E14] rounded-md px-4 py-2.5 text-sm font-medium transition-colors" data-testid="apply-coupon-btn">Apply</button>
                    </div>
                    {couponApplied && <p className="text-[#10B981] text-xs mt-2">Coupon applied: -{couponApplied.type === 'free_delivery' ? 'Free delivery' : `$${couponDiscount.toFixed(2)}`}</p>}
                  </div>

                  {/* Loyalty Points */}
                  {user && loyaltyPoints > 0 && (
                    <div className="bg-[#12151C] border border-white/5 rounded-xl p-5">
                      <h3 className="text-white font-medium mb-3 flex items-center gap-2"><Award className="w-4 h-4 text-[#b56b38]" /> Loyalty Points</h3>
                      <p className="text-[#94A3B8] text-sm mb-3">You have <span className="text-[#b56b38] font-medium">{loyaltyPoints} points</span> (${(loyaltyPoints / 100).toFixed(2)} value)</p>
                      <div className="flex items-center gap-3">
                        <input type="range" min={0} max={Math.min(loyaltyPoints, Math.floor(subtotal * 100))} step={100} value={redeemPoints} onChange={e => setRedeemPoints(parseInt(e.target.value))} className="flex-1 accent-[#cc3737]" data-testid="loyalty-slider" />
                        <span className="text-white text-sm font-medium w-24 text-right">-${loyaltyDiscount.toFixed(2)} ({redeemPoints} pts)</span>
                      </div>
                    </div>
                  )}

                  {/* Age Verification — MANDATORY for alcohol orders */}
                  {hasAlcohol && (
                    <div className={`border rounded-xl p-5 transition-colors ${ageConfirmed ? 'bg-[#10B981]/10 border-[#10B981]/40' : 'bg-[#EF4444]/10 border-[#EF4444]/40'}`} data-testid="age-verification-box">
                      <h3 className="text-white font-medium mb-2 flex items-center gap-2">
                        <ShieldAlert className={`w-4 h-4 ${ageConfirmed ? 'text-[#10B981]' : 'text-[#EF4444]'}`} />
                        Age Verification Required
                      </h3>
                      <p className="text-[#94A3B8] text-sm mb-3">
                        Your order contains alcohol. By law, the recipient must be <span className="text-white font-medium">21 years or older</span> with a valid government-issued ID at the time of delivery.
                      </p>
                      <label className="flex items-start gap-3 cursor-pointer bg-[#0B0E14] border border-white/10 rounded-lg px-4 py-3 hover:border-[#b56b38]/40 transition-colors">
                        <input
                          type="checkbox"
                          checked={ageConfirmed}
                          onChange={e => setAgeConfirmed(e.target.checked)}
                          className="mt-0.5 w-5 h-5 accent-[#cc3737] cursor-pointer flex-shrink-0"
                          data-testid="age-confirm-checkbox"
                        />
                        <span className="text-white text-sm leading-relaxed">
                          I confirm that I am <span className="font-semibold text-[#b56b38]">21 years of age or older</span>, the delivery recipient will also be 21+, and I agree to present a valid photo ID upon delivery. I understand the driver may refuse delivery without a valid ID and the order may be cancelled.
                        </span>
                      </label>
                    </div>
                  )}

                  {/* Place Order */}
                </div>
              )}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-2">
              <div className="bg-[#12151C] border border-white/10 rounded-xl p-5 sticky top-24">
                <h3 className="font-['Playfair_Display'] text-lg text-white mb-4">Order Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-[#94A3B8]">Subtotal ({items.reduce((s, i) => s + i.quantity, 0)} items)</span><span className="text-white">${subtotal.toFixed(2)}</span></div>
                  {couponDiscount > 0 && <div className="flex justify-between"><span className="text-[#10B981]">Coupon Discount</span><span className="text-[#10B981]">-${couponDiscount.toFixed(2)}</span></div>}
                  {loyaltyDiscount > 0 && <div className="flex justify-between"><span className="text-[#10B981]">Loyalty Points</span><span className="text-[#10B981]">-${loyaltyDiscount.toFixed(2)}</span></div>}
                  {storeInfo.tax_enabled ? (
                    <div className="flex justify-between"><span className="text-[#94A3B8]">{storeInfo.tax_label || 'Tax'} ({((storeInfo.tax_rate || 0) * 100).toFixed(3)}%)</span><span className="text-white">${tax.toFixed(2)}</span></div>
                  ) : (
                    <div className="flex justify-between"><span className="text-[#64748B]">Tax</span><span className="text-[#64748B]">Not applied</span></div>
                  )}
                  {!isPickup && <div className="flex justify-between"><span className="text-[#94A3B8]">Delivery Fee</span><span className={deliveryFee === 0 ? 'text-[#10B981]' : 'text-white'}>{deliveryFee === 0 ? 'FREE' : `$${deliveryFee.toFixed(2)}`}</span></div>}
                  {isPickup && <div className="flex justify-between"><span className="text-[#94A3B8]">Pickup at Store</span><span className="text-[#10B981]">FREE</span></div>}
                  {step === 'checkout' && !isPickup && <div className="flex justify-between"><span className="text-[#94A3B8]">Tip</span><span className="text-white">${tipValue.toFixed(2)}</span></div>}
                  {step === 'checkout' && paymentMethod === 'cod' && <div className="flex justify-between text-[#b56b38]"><span>Payment</span><span>Cash {isPickup ? 'at pickup' : 'on delivery'}</span></div>}
                  <div className="border-t border-white/10 pt-2 mt-2 flex justify-between"><span className="text-white font-medium">Total</span><span className="text-[#b56b38] font-semibold text-lg">${total.toFixed(2)}</span></div>
                </div>

                {step === 'cart' ? (
                  <button onClick={() => setStep('checkout')} className="w-full bg-[#cc3737] hover:bg-[#d94444] text-white rounded-full py-3 font-medium transition-colors mt-6" data-testid="proceed-checkout-btn">
                    Proceed to Checkout
                  </button>
                ) : (
                  <button onClick={handlePlaceOrder} disabled={placing || (hasAlcohol && !ageConfirmed)} className="w-full bg-[#cc3737] hover:bg-[#d94444] disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-full py-3 font-medium transition-colors mt-6 flex items-center justify-center gap-2" data-testid="place-order-btn">
                    {placing ? 'Placing Order...' : (hasAlcohol && !ageConfirmed) ? 'Confirm 21+ to Continue' : `Place Order — $${total.toFixed(2)}`}
                  </button>
                )}

                <p className="text-[#64748B] text-xs text-center mt-4 font-light">
                  {hasAlcohol ? 'Valid photo ID (21+) required at delivery' : 'Secure checkout'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
